import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr as frLocale, enGB } from "date-fns/locale";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, Loader2, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { OrnamentDivider } from "@/components/OrnamentDivider";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { Florals } from "@/components/site/Florals";
import { createWedding, errorMessage, setToken } from "@/lib/api";
import { currentLanguage, LANGUAGES, t } from "@/i18n";

const FieldLabel = ({ htmlFor, children }) => (
  <Label
    htmlFor={htmlFor}
    className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[color:var(--muted-ink)]"
  >
    {children}
  </Label>
);

const Stepper = ({ label, value, onChange, min, max, testId, hint }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div className="mt-2 flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-10 w-10"
        onClick={() => onChange(Math.max(min, value - 1))}
        data-testid={`${testId}-minus`}
        aria-label={`${t("create.less")} ${label}`}
      >
        <Minus className="h-4 w-4" />
      </Button>
      <Input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (!Number.isNaN(next)) onChange(Math.min(max, Math.max(min, next)));
        }}
        className="font-num h-10 w-20 bg-white text-center"
        data-testid={testId}
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-10 w-10"
        onClick={() => onChange(Math.min(max, value + 1))}
        data-testid={`${testId}-plus`}
        aria-label={`${t("create.more")} ${label}`}
      >
        <Plus className="h-4 w-4" />
      </Button>
      {hint ? <span className="text-[12px] text-[color:var(--muted-ink)]">{hint}</span> : null}
    </div>
  </div>
);

const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

export default function CreateWedding() {
  const navigate = useNavigate();
  const uiLang = currentLanguage();
  const [coupleNames, setCoupleNames] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [lang, setLang] = useState(uiLang);
  const [venue, setVenue] = useState("");
  const [date, setDate] = useState(null);
  const [tables, setTables] = useState(10);
  const [seats, setSeats] = useState(10);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!coupleNames.trim()) {
      toast.error(t("create.whose"));
      return;
    }
    if (contactEmail.trim() && !looksLikeEmail(contactEmail)) {
      toast.error(t("create.badEmail"));
      return;
    }
    if (password.trim().length < 8) {
      toast.error(t("create.shortPassword"));
      return;
    }
    setSaving(true);
    try {
      const payload = await createWedding({
        couple_names: coupleNames.trim(),
        contact_email: contactEmail.trim(),
        lang,
        venue: venue.trim(),
        wedding_date: date ? format(date, "yyyy-MM-dd") : "",
        n_tables: tables,
        seats_per_table: seats,
        password: password.trim(),
      });
      setToken(payload.wedding.id, payload.token);
      toast.success(t("create.created"));
      navigate(`/w/${payload.wedding.id}/admin?tab=guests`);
    } catch (error) {
      toast.error(errorMessage(error, t("create.failed")));
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="hero-wash relative overflow-hidden border-b border-[color:var(--line)]">
        <Florals corner="top-right" className="opacity-70" />
        <div className="relative mx-auto flex max-w-[820px] items-center justify-between px-6 py-5">
          <Link to="/" className="font-display text-xl font-semibold">
            {t("app.name")}
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitch />
            <Button variant="ghost" asChild className="text-[13px]">
              <Link to="/" data-testid="back-home-link">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> {t("create.back")}
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[820px] px-6 py-12">
        <p className="eyebrow">{t("create.step")}</p>
        <h1 className="mt-2 text-4xl font-semibold leading-tight">{t("create.title")}</h1>
        <p className="mt-3 max-w-xl text-[14.5px] leading-relaxed text-[color:var(--ink-soft)]">
          {t("create.intro")}
        </p>

        <form onSubmit={submit} className="surface mt-8 space-y-6 rounded-xl p-6">
          <div>
            <FieldLabel htmlFor="couple">{t("create.couple")}</FieldLabel>
            <Input
              id="couple"
              value={coupleNames}
              onChange={(event) => setCoupleNames(event.target.value)}
              placeholder={t("create.couplePlaceholder")}
              className="mt-2 h-11 bg-white text-[15px]"
              data-testid="couple-names-input"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-[1fr_auto]">
            <div>
              <FieldLabel htmlFor="contact-email">{t("create.email")}</FieldLabel>
              <p className="mt-1 text-[12.5px] text-[color:var(--ink-soft)]">{t("create.emailHint")}</p>
              <Input
                id="contact-email"
                type="email"
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
                placeholder="amelie.julien@example.fr"
                autoComplete="email"
                className="mt-2 h-11 bg-white text-[15px]"
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <FieldLabel>{t("create.language")}</FieldLabel>
              <p className="mt-1 text-[12.5px] text-[color:var(--ink-soft)]">{t("create.languageHint")}</p>
              <div
                className="mt-2 inline-flex gap-0.5 rounded-full p-0.5"
                style={{ backgroundColor: "var(--paper-2)" }}
                data-testid="wedding-language"
              >
                {Object.entries(LANGUAGES).map(([code, label]) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLang(code)}
                    aria-pressed={lang === code}
                    data-testid={`wedding-language-${code}`}
                    className="h-10 rounded-full px-4 text-[13px] font-medium"
                    style={{
                      backgroundColor: lang === code ? "#ffffff" : "transparent",
                      color: lang === code ? "var(--ink)" : "var(--muted-ink)",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <FieldLabel htmlFor="wedding-password">{t("create.password")}</FieldLabel>
            <p className="mt-1 text-[12.5px] text-[color:var(--ink-soft)]">{t("create.passwordHint")}</p>
            <Input
              id="wedding-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t("create.passwordPlaceholder")}
              autoComplete="new-password"
              className="mt-2 h-11 bg-white"
              data-testid="create-password-input"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <FieldLabel htmlFor="venue">{t("create.venue")}</FieldLabel>
              <Input
                id="venue"
                value={venue}
                onChange={(event) => setVenue(event.target.value)}
                placeholder={t("create.venuePlaceholder")}
                className="mt-2 h-11 bg-white text-[15px]"
                data-testid="venue-input"
              />
            </div>
            <div>
              <FieldLabel>{t("create.date")}</FieldLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-2 h-11 w-full justify-start bg-white text-[15px] font-normal"
                    data-testid="wedding-date-trigger"
                  >
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {date
                      ? format(date, "d MMMM yyyy", { locale: uiLang === "fr" ? frLocale : enGB })
                      : t("create.pickDate")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                    locale={uiLang === "fr" ? frLocale : enGB}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <OrnamentDivider />

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Stepper
              label={t("create.tables")}
              value={tables}
              onChange={setTables}
              min={1}
              max={60}
              testId="tables-input"
            />
            <Stepper
              label={t("create.seats")}
              value={seats}
              onChange={setSeats}
              min={2}
              max={24}
              testId="seats-input"
            />
          </div>
          <p className="font-num text-[13px] text-[color:var(--ink-soft)]" data-testid="seats-total">
            {tables} × {seats} = {tables * seats} {t("create.seatsWord")}
          </p>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              type="submit"
              size="lg"
              disabled={saving}
              className="h-11 rounded-xl px-6"
              data-testid="create-wedding-submit-button"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("create.submit")}
            </Button>
            <Link to="/demo" className="link-underline text-[13px] text-[color:var(--ink-soft)]">
              {t("create.orDemo")}
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
